import { gql, useMutation, useQuery } from '@apollo/client';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { all, and, equals, isEmpty, map, not, or } from 'ramda';
import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled, { ThemeProvider } from 'styled-components';

import FlowFooter from '@ui/atoms/FlowFooter';
import MembershipContainer from '@ui/atoms/MembershipContainer';

import NewFlowHeader from '@ui/organisms/NewFlowHeader';
import PageContainer from '@ui/organisms/PageContainer';

import useFlowData from '@hooks/useFlowData';
import { useMemberValidation } from '@hooks/useMemberValidation';
import { useTracking } from '@hooks/useTracking';

import {
	getFinalStepIndex,
	getFirstSecureStep,
	getFirstStep,
	getIntermediateSteps,
	getNextStepForKey,
	getPathForCurrentStep,
	getPathPrefixFromPathname,
	getPreviousStepForKey,
	getStepForKey,
	getStepForPath,
	getStepIndex,
	getStepKeysForSteps,
	getStepsForFlow,
} from '@utils/flowHelpers';
import { exists } from '@utils/general';

import { useAuth } from '@providers/AuthProvider';
import { FlowContextProvider } from '@providers/FlowContextProvider';

import { FlowIdTypes, FormData } from '@folx-types/flow';

import completedMembership from '@graphql/queries/completedOnboardingQuery.gql';
import currentMemberWithSubscriptionsQuery from '@graphql/queries/currentMemberAllSubscriptions.gql';

import { getThemeForFlow } from '@theme/FlowThemes';
import theme from '@theme/FolxTheme';

interface IMembershipRouteProps {
	path: string;
	children?: ReactNode;
}

interface ICompletedMembershipRouteQuery {
	memberFlowCompletions: {
		nodes: Array<{
			memberId: string;
			flowName: string;
			version: string;
			createdDt: string;
		}>;
	};
}

const completeFlowMutation = gql`
	mutation CompleteFlow {
		completeFlow(input: { clientMutationId: "completeFlow" }) {
			clientMutationId
		}
	}
`;

const FLOW_ID: FlowIdTypes = 'membership';
const FLOW_PATH = 'membership';

const MembershipRoute: FC<IMembershipRouteProps> = ({ children: nestedRouter, ...props }) => {
	const location = useLocation();
	const currentPath = getPathForCurrentStep(props['*']);
	const { loggedIn, initialized: authInitialized, userId } = useAuth();

	const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);

	const {
		props: { children: routes },
	} = nestedRouter;

	const stepsForFlow = getStepsForFlow(routes);

	const currentStep = getStepForPath(currentPath, stepsForFlow);
	const firstStep = getFirstStep(stepsForFlow);
	const firstSecureStep = getFirstSecureStep(stepsForFlow);
	const exceptionSteps = getIntermediateSteps(stepsForFlow);

	const currentIndex = getStepIndex(currentStep!, stepsForFlow);
	const finalIndex = getFinalStepIndex(stepsForFlow);

	const [loaded, setLoaded] = useState(false);

	const { loading: currentMemberWithSubsLoading, data: currentMemberWithSubsData } = useQuery(
		currentMemberWithSubscriptionsQuery,
		{
			skip: !loggedIn,
		}
	);

	const [completeFlow] = useMutation(completeFlowMutation, {
		onCompleted: () => navigate(`/${pathPrefix}/membership/success`),
		awaitRefetchQueries: true,
		refetchQueries: [
			{
				query: completedMembership,
				variables: {
					flowName: 'membership',
					memberId: userId,
				},
			},
		],
	});

	const { loading: completedMembershipLoading, data: completedMembershipData } =
		useQuery<ICompletedMembershipRouteQuery>(completedMembership, {
			variables: {
				flowName: 'membership',
				memberId: userId,
			},
			skip: !loggedIn,
		});

	const { loaded: loadedForms, formData, submitFormData } = useFlowData(FLOW_ID);
	const {
		memberMetadata,
		validateMember,
		loaded: memberMetadataLoaded,
	} = useMemberValidation(formData, loadedForms);

	const { trackEvent } = useTracking();
	useEffect(() => {
		trackEvent('view-onboarding-step', {
			step_name: currentStep?.stepKey,
			flow_id: FLOW_ID,
		});
	}, [currentStep]);

	useEffect(() => {
		if (
			authInitialized &&
			!currentMemberWithSubsLoading &&
			!completedMembershipLoading &&
			loadedForms &&
			memberMetadataLoaded
		) {
			setLoaded(true);
		}
	}, [
		authInitialized,
		currentMemberWithSubsLoading,
		completedMembershipLoading,
		loadedForms,
		memberMetadataLoaded,
	]);

	useDeepCompareEffect(() => {
		const exceptionStepKeys = getStepKeysForSteps(exceptionSteps);

		if (authInitialized && loaded) {
			const allSubNames = map(
				(n) => n.subscriptionType,
				currentMemberWithSubsData?.currentMember.memberSubscriptions?.nodes ?? []
			);

			if (currentStep?.stepKey === 'SUCCESS') {
				return;
			}

			if (exceptionStepKeys.includes(currentStep?.stepKey)) {
				return;
			}
			if (and(loggedIn, memberMetadata.account.isLegacyUser)) {
				validateMember();
				return;
			}
			if (and(not(loggedIn), not(equals(currentStep?.stepKey, firstStep.stepKey)))) {
				navigate(`/${pathPrefix}/membership/${firstStep.path}${location.search}`, {
					replace: true,
				});
				return;
			}
			if (
				all(equals(true), [
					loggedIn,
					or(
						allSubNames?.includes('MEMBERSHIP'),
						exists(completedMembershipData?.memberFlowCompletions?.nodes)
					),
					or(
						memberMetadata?.membership?.isMembershipActive,
						memberMetadata?.membership?.isMembershipPastDue
					),
					memberMetadata?.account?.isOnboardingComplete,
				])
			) {
				navigate(`/${pathPrefix}/membership/success`, { replace: true });
				return;
			}
			if (
				all(equals(true), [
					loggedIn,
					memberMetadata?.membership?.isMembershipIncomplete,
					memberMetadata?.account?.hasFullDemographic,
					memberMetadata?.account?.hasAddress,
				])
			) {
				navigate(`/${pathPrefix}/membership/insurance`, { replace: true });
				return;
			}
			if (and(loggedIn, not(equals(currentStep?.stepKey, firstSecureStep.stepKey)))) {
				navigate(`/${pathPrefix}/membership/${firstSecureStep.path}`, {
					replace: true,
				});
				return;
			}
		}
	}, [authInitialized, loggedIn, loaded]);

	const handleFormSubmission = async (
		data: FormData,
		stepKey: string,
		proceedNext = true,
		params?: string
	) => {
		if (loggedIn && !isEmpty(data)) {
			await submitFormData(data);
		}

		if (proceedNext) {
			const currentStepForKey = getStepForKey(stepsForFlow, stepKey);
			const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
			if (currentStepForKey?.finalStep) {
				await completeFlow();
				return;
			}
			console.log(`Step Key: ${stepKey}`);
			const nextStep = getNextStepForKey(stepsForFlow, FLOW_PATH, stepKey, {
				...formData,
				...data,
			});

			let to = `/${pathPrefix}/${FLOW_PATH}/${nextStep.path}`;

			if (params?.length) {
				to += params;
			}

			console.log(`Next step: ${JSON.stringify(nextStep)}`);
			navigate(to);
		}
	};

	const handleNavBack = (stepKey: string) => {
		const previousStep = getPreviousStepForKey(stepsForFlow, stepKey);
		navigate(`/${pathPrefix}/${FLOW_PATH}/${previousStep.path}`, {
			state: { reverse: true },
		});
	};

	const handleNavNext = (currentStepKey: string) => {
		const nextStep = getNextStepForKey(stepsForFlow, FLOW_ID, currentStepKey, formData);
		navigate(`/${pathPrefix}/${FLOW_PATH}/${nextStep.path}`, {
			state: { reverse: false },
		});
	};

	return (
		<FlowContextProvider
			value={{
				destinationFlow: FLOW_PATH,
				existingFormData: formData,
				flowId: FLOW_ID,
				handleFormSubmission,
				handleNavBack,
				handleNavNext,
				submitFormData,
				flow: FLOW_PATH,
			}}>
			<ThemeProvider theme={getThemeForFlow('onboarding')}>
				<PageContainer
					header={
						<NewFlowHeader
							flowName="Membership"
							totalSteps={finalIndex}
							step={currentIndex}
							showSignIn={currentStep?.stepKey === 'ELIGIBILITY'}
						/>
					}
					footer={<FlowFooter stepKey={currentStep?.stepKey} />}>
					{loaded ? <MembershipContainer>{nestedRouter}</MembershipContainer> : <div />}
				</PageContainer>
			</ThemeProvider>
		</FlowContextProvider>
	);
};

export default MembershipRoute;
